export const APP_THEME_STORAGE_KEY = "billsarthi_app_theme_v1";
export const APP_THEME_EVENT = "billsarthi:theme-change";
export const APP_THEME_OPTIONS = ["light", "dark", "system"];

export const normalizeAppTheme = (value) =>
  APP_THEME_OPTIONS.includes(value) ? value : "system";

export const readAppTheme = () => {
  if (typeof window === "undefined") return "system";
  return normalizeAppTheme(window.localStorage.getItem(APP_THEME_STORAGE_KEY));
};

export const resolveAppTheme = (preference, prefersDark = false) =>
  normalizeAppTheme(preference) === "system"
    ? (prefersDark ? "dark" : "light")
    : normalizeAppTheme(preference);

export const applyAppTheme = (preference = readAppTheme()) => {
  if (typeof document === "undefined") return "light";
  const prefersDark = Boolean(
    typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-color-scheme: dark)")?.matches
  );
  const normalized = normalizeAppTheme(preference);
  const resolved = resolveAppTheme(normalized, prefersDark);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.dataset.themePreference = normalized;
  document.documentElement.style.colorScheme = resolved;
  return resolved;
};

export const saveAppTheme = (preference) => {
  const normalized = normalizeAppTheme(preference);
  window.localStorage.setItem(APP_THEME_STORAGE_KEY, normalized);
  const resolved = applyAppTheme(normalized);
  window.dispatchEvent(
    new CustomEvent(APP_THEME_EVENT, { detail: { preference: normalized, resolved } })
  );
  return resolved;
};

export const nextAppTheme = (preference) => {
  const index = APP_THEME_OPTIONS.indexOf(normalizeAppTheme(preference));
  return APP_THEME_OPTIONS[(index + 1) % APP_THEME_OPTIONS.length];
};

const parseRgb = (value) => {
  const match = String(value || "").match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  return match
    ? { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]), a: Number(match[4] ?? 1) }
    : null;
};

const channelLuminance = (channel) => {
  const value = channel / 255;
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
};

const luminance = ({ r, g, b }) =>
  0.2126 * channelLuminance(r) +
  0.7152 * channelLuminance(g) +
  0.0722 * channelLuminance(b);

const isBlueTone = (color) => {
  if (!color || color.a < 0.35) return false;
  const { h, s } = rgbToHsl(color);
  return h >= 175 && h <= 230 && s >= 34;
};

const colorsFromCssValue = (value) => {
  const colors = [];
  String(value || "").replace(
    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/g,
    (match, r, g, b, a) => {
      colors.push({ r: Number(r), g: Number(g), b: Number(b), a: Number(a ?? 1) });
      return match;
    }
  );
  return colors;
};

const rgbToHsl = ({ r, g, b }) => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: lightness };
  const delta = max - min;
  const saturation = lightness > 0.5
    ? delta / (2 - max - min)
    : delta / (max + min);
  let hue = max === red
    ? (green - blue) / delta + (green < blue ? 6 : 0)
    : max === green
      ? (blue - red) / delta + 2
      : (red - green) / delta + 4;
  hue /= 6;
  return { h: hue * 360, s: saturation * 100, l: lightness * 100 };
};

const darkSurfaceColor = (color) => {
  const { l } = rgbToHsl(color);
  if (l >= 97) return "#15191f";
  if (l >= 92) return "#1c2128";
  return "#242a32";
};

const darkTextColor = (color) => {
  const { l } = rgbToHsl(color);
  return l < 28 ? "#f1f3f5" : "#b8bec7";
};

const darkBorderColor = () => "#424953";

const isLargeNonInteractiveSurface = (element) => {
  if (element.matches("button, a, input, select, textarea, [role='button']")) return false;
  const { width, height } = element.getBoundingClientRect();
  return width >= 280 && height >= 54;
};

const buildDarkBackgroundImage = (value) => {
  const colors = [];
  String(value || "").replace(
    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/g,
    (match, r, g, b, a) => {
      colors.push({ match, color: { r: Number(r), g: Number(g), b: Number(b), a: Number(a ?? 1) } });
      return match;
    }
  );
  const visibleColors = colors.filter(({ color }) => color.a >= 0.55);
  if (!visibleColors.length || visibleColors.some(({ color }) => luminance(color) < 0.62)) {
    return null;
  }
  let transformed = String(value);
  visibleColors.forEach(({ match, color }) => {
    transformed = transformed.replace(match, darkSurfaceColor(color));
  });
  return transformed;
};

const tagLegacyThemeColors = (root = document.body) => {
  if (!root || typeof window === "undefined") return;
  const elements = root === document.documentElement
    ? [document.body, ...document.body.querySelectorAll("*")]
    : [root, ...root.querySelectorAll("*")];
  elements.forEach((element) => {
    if (!(element instanceof HTMLElement) || element.closest("[data-theme-ignore]")) return;
    const style = window.getComputedStyle(element);
    const background = parseRgb(style.backgroundColor);
    const foreground = parseRgb(style.color);
    const border = parseRgb(style.borderTopColor);
    const darkBackgroundImage = style.backgroundImage !== "none"
      ? buildDarkBackgroundImage(style.backgroundImage)
      : null;
    const largeSurface = isLargeNonInteractiveSurface(element);
    const saturatedLargeSurface = Boolean(
      largeSurface &&
      background?.a > 0.72 &&
      rgbToHsl(background).s >= 20
    );
    const largeGradientSurface = largeSurface && style.backgroundImage !== "none";
    const isButton = element.matches("button, [role='button'], input[type='button'], input[type='submit']");
    const gradientColors = colorsFromCssValue(style.backgroundImage);
    const blueButton = isButton && (
      isBlueTone(background) ||
      isBlueTone(foreground) ||
      gradientColors.some(isBlueTone)
    );

    if (blueButton) {
      element.dataset.appThemeBlueButton = background?.a > 0.35 || gradientColors.length
        ? "filled"
        : "outlined";
    }

    if (
      background?.a > 0.72 &&
      (luminance(background) >= 0.70 || saturatedLargeSurface || largeGradientSurface)
    ) {
      element.dataset.appThemeSurface = "true";
      element.style.setProperty(
        "--app-dark-surface",
        saturatedLargeSurface || largeGradientSurface
          ? "var(--app-surface)"
          : darkSurfaceColor(background)
      );
    }
    if (darkBackgroundImage || largeGradientSurface) {
      element.dataset.appThemeBackgroundImage = "true";
      element.style.setProperty(
        "--app-dark-background-image",
        largeGradientSurface
          ? "linear-gradient(135deg, var(--app-surface-raised), var(--app-surface))"
          : darkBackgroundImage
      );
    }
    if (foreground?.a > 0.72 && luminance(foreground) <= 0.32) {
      element.dataset.appThemeText = "true";
      element.style.setProperty("--app-dark-text", darkTextColor(foreground));
    }
    if (border?.a > 0.35 && luminance(border) >= 0.48) {
      element.dataset.appThemeBorder = "true";
      element.style.setProperty("--app-dark-border", darkBorderColor(border));
    }
  });
};

export const installAppTheme = () => {
  let scanFrame = 0;
  const followupTimers = new Set();
  const pendingRoots = new Set();
  const scheduleLegacyScan = (root = document.documentElement) => {
    if (root instanceof HTMLElement) pendingRoots.add(root);
    window.cancelAnimationFrame(scanFrame);
    scanFrame = window.requestAnimationFrame(() => {
      const roots = Array.from(pendingRoots);
      pendingRoots.clear();
      roots.forEach((scanRoot) => tagLegacyThemeColors(scanRoot));
    });
  };
  const refresh = () => {
    const preference = readAppTheme();
    const resolved = applyAppTheme(preference);
    window.dispatchEvent(
      new CustomEvent(APP_THEME_EVENT, { detail: { preference, resolved } })
    );
    scheduleLegacyScan();
    [120, 500].forEach((delay) => {
      const timer = window.setTimeout(() => {
        followupTimers.delete(timer);
        scheduleLegacyScan();
      }, delay);
      followupTimers.add(timer);
    });
  };
  refresh();
  const media = window.matchMedia?.("(prefers-color-scheme: dark)");
  media?.addEventListener?.("change", refresh);
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) pendingRoots.add(node);
      });
    });
    if (pendingRoots.size) scheduleLegacyScan(pendingRoots.values().next().value);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener(APP_THEME_EVENT, scheduleLegacyScan);
  return () => {
    media?.removeEventListener?.("change", refresh);
    window.removeEventListener(APP_THEME_EVENT, scheduleLegacyScan);
    window.cancelAnimationFrame(scanFrame);
    followupTimers.forEach((timer) => window.clearTimeout(timer));
    followupTimers.clear();
    observer.disconnect();
  };
};
